/* eslint-disable @typescript-eslint/no-explicit-any */

import mapboxgl, { type Map as MapboxMap, type Popup } from "mapbox-gl";
import { MAP_BUILDING_COLOR_MODE_LIGHT } from "../config/mapColors";

/**
 * Callback function to generate tooltip content from feature properties
 */
export type GetTooltipContentHandler = (
  properties: Record<string, unknown>,
) => string | null;

/**
 * Callback function to generate opacity for a GeoJSON feature
 * @returns opacity value between 0 and 1, or null to use default
 */
export type GetGeoJSONFeatureOpacityHandler = (
  properties: Record<string, unknown>,
) => number | null;

// Store popup instance and event handlers for cleanup
let activePopup: Popup | null = null;
let mouseEnterHandler: ((e: any) => void) | null = null;
let mouseMoveHandler: ((e: any) => void) | null = null;
let mouseLeaveHandler: (() => void) | null = null;

export type Add3DBuildingsOptions = {
  /**
   * Color of the 3D buildings (default: "#CCCCCC")
   */
  color?: string;
  /**
   * Opacity of the 3D buildings (0-1, default: 1)
   */
  opacity?: number;
  /**
   * Layer ID to insert before (optional)
   */
  beforeLayerId?: string;
  /**
   * Whether to make the layer visible (default: false)
   */
  visible?: boolean;
};

/**
 * Adds 3D building extrusions to the map
 * @param map - Mapbox map instance
 * @param options - Configuration options for 3D buildings
 */
export const add3DBuildings = (
  map: MapboxMap,
  options: Add3DBuildingsOptions = {},
) => {
  const {
    color = MAP_BUILDING_COLOR_MODE_LIGHT,
    opacity = 1,
    beforeLayerId,
    visible = true,
  } = options;

  try {
    if (!map.getLayer("3d-buildings")) {
      map.addLayer(
        {
          id: "3d-buildings",
          source: "composite",
          "source-layer": "building",
          type: "fill-extrusion",
          paint: {
            "fill-extrusion-color": color,
            "fill-extrusion-height": ["number", ["get", "height"], 5],
            "fill-extrusion-base": ["number", ["get", "min_height"], 0],
            "fill-extrusion-opacity": opacity,
          },
          layout: {
            visibility: visible ? "visible" : "none",
          },
        },
        beforeLayerId,
      );
    }
  } catch (error) {
    console.error("Error adding 3D buildings layer:", error);
  }
};

/**
 * Removes all symbol layers (labels, icons, etc.) from the map
 * @param map - Mapbox map instance
 * @returns Object containing labelLayerId if found and not removed
 */
export const removeSymbolLayers = (map: any): { labelLayerId?: string } => {
  const layers = map.getStyle().layers;
  let labelLayerId: string | undefined;

  for (const layer of layers) {
    if (layer.type === "symbol") {
      map.removeLayer(layer.id);
    }
  }

  return { labelLayerId };
};

/**
 * Finds the first label layer ID without removing symbol layers
 * Useful when you want to preserve labels but need the layer ID for insertion
 * @param map - Mapbox map instance
 * @returns Object containing labelLayerId if found
 */
export const findLabelLayer = (map: any): { labelLayerId?: string } => {
  const layers = map.getStyle().layers;
  let labelLayerId: string | undefined;

  for (const layer of layers) {
    if (
      layer.type === "symbol" &&
      layer.layout?.["text-field"] &&
      !labelLayerId
    ) {
      labelLayerId = layer.id;
      break;
    }
  }

  return { labelLayerId };
};

/**
 * Removes symbol layers conditionally and finds label layer ID
 * @param map - Mapbox map instance
 * @param shouldRemove - Whether to remove symbol layers
 * @returns Object containing labelLayerId if found and not removed
 */
export const handleSymbolLayers = (
  map: any,
  shouldRemove: boolean,
): { labelLayerId?: string } => {
  const layers = map.getStyle().layers;
  let labelLayerId: string | undefined;

  for (const layer of layers) {
    if (layer.type === "symbol") {
      if (shouldRemove) {
        map.removeLayer(layer.id);
      } else if (layer.layout?.["text-field"] && !labelLayerId) {
        labelLayerId = layer.id;
      }
    }
  }

  return { labelLayerId };
};

/**
 * Removes GeoJSON layers, source, popup, and event handlers from the map
 * @param map - Mapbox map instance
 */
export const removeGeoJSONLayers = (map: any) => {
  // Remove event handlers
  if (mouseEnterHandler) {
    map.off("mouseenter", "geojson-layer", mouseEnterHandler);
    mouseEnterHandler = null;
  }
  if (mouseMoveHandler) {
    map.off("mousemove", "geojson-layer", mouseMoveHandler);
    mouseMoveHandler = null;
  }
  if (mouseLeaveHandler) {
    map.off("mouseleave", "geojson-layer", mouseLeaveHandler);
    mouseLeaveHandler = null;
  }

  // Remove popup
  if (activePopup) {
    activePopup.remove();
    activePopup = null;
  }

  if (map.getLayer("geojson-outline")) {
    map.removeLayer("geojson-outline");
  }
  if (map.getLayer("geojson-layer")) {
    map.removeLayer("geojson-layer");
  }
  if (map.getSource("geojson-data")) {
    map.removeSource("geojson-data");
  }
};

/** Default opacity for GeoJSON fill layer */
const DEFAULT_GEOJSON_FILL_OPACITY = 0.1;

/**
 * Pre-processes GeoJSON features to add opacity property based on handler
 * @param geoJSON - Original GeoJSON feature collection
 * @param getOpacity - Handler to calculate opacity per feature
 * @returns New GeoJSON with _opacity property added to each feature
 */
const preprocessGeoJSONWithOpacity = (
  geoJSON: GeoJSON.FeatureCollection,
  getOpacity: GetGeoJSONFeatureOpacityHandler,
): GeoJSON.FeatureCollection => ({
  ...geoJSON,
  features: geoJSON.features.map((feature) => ({
    ...feature,
    properties: {
      ...feature.properties,
      _opacity:
        getOpacity(feature.properties as Record<string, unknown>) ??
        DEFAULT_GEOJSON_FILL_OPACITY,
    },
  })),
});

/**
 * Adds GeoJSON layers to the map with optional tooltips
 * @param map - Mapbox map instance
 * @param geoJSON - GeoJSON feature collection
 * @param accentColor - Color for the GeoJSON fill and outline
 * @param getTooltipContent - Optional callback to generate tooltip content
 * @param getFeatureOpacity - Optional callback to generate opacity per feature
 */
export const addGeoJSONLayers = (
  map: any,
  geoJSON: GeoJSON.FeatureCollection,
  accentColor: string,
  getTooltipContent?: GetTooltipContentHandler,
  getFeatureOpacity?: GetGeoJSONFeatureOpacityHandler,
) => {
  try {
    // Remove existing layers if they exist
    removeGeoJSONLayers(map);

    // Pre-process GeoJSON with opacity if handler provided
    const processedGeoJSON = getFeatureOpacity
      ? preprocessGeoJSONWithOpacity(geoJSON, getFeatureOpacity)
      : geoJSON;

    map.addSource("geojson-data", {
      type: "geojson",
      data: processedGeoJSON,
    });

    map.addLayer({
      id: "geojson-layer",
      type: "fill",
      source: "geojson-data",
      paint: {
        "fill-color": accentColor,
        "fill-opacity": getFeatureOpacity
          ? ["get", "_opacity"]
          : DEFAULT_GEOJSON_FILL_OPACITY,
      },
    });

    map.addLayer({
      id: "geojson-outline",
      type: "line",
      source: "geojson-data",
      paint: {
        "line-color": accentColor,
        "line-width": 2,
      },
    });

    // Add tooltip functionality if getTooltipContent is provided
    if (getTooltipContent) {
      activePopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      mouseEnterHandler = (e: any) => {
        map.getCanvas().style.cursor = "pointer";

        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const content = getTooltipContent(feature.properties);

          if (content && activePopup) {
            activePopup.setLngLat(e.lngLat).setHTML(content).addTo(map);
          }
        }
      };

      mouseMoveHandler = (e: any) => {
        if (!activePopup) return;

        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const content = getTooltipContent(feature.properties);

          if (content) {
            activePopup.setLngLat(e.lngLat).setHTML(content);
            if (!activePopup.isOpen()) {
              activePopup.addTo(map);
            }
          }
        }
      };

      mouseLeaveHandler = () => {
        map.getCanvas().style.cursor = "";
        if (activePopup) {
          activePopup.remove();
        }
      };

      map.on("mouseenter", "geojson-layer", mouseEnterHandler);
      map.on("mousemove", "geojson-layer", mouseMoveHandler);
      map.on("mouseleave", "geojson-layer", mouseLeaveHandler);
    }
  } catch (error) {
    console.error("Error adding GeoJSON layers:", error);
  }
};
